import React, { useState, useEffect } from 'react';
import './Appointments.css';
import SideBar from '../SideBar/SideBar';
import adminAppointmentsService from '../../services/adminAppointments.service';
import appointmentService from '../../services/appointment.service';

interface Appointment {
    id: number;
    customerName: string;
    date: string;
    time: string;
    service: string;
    staffName: string;
    duration: number;
    cost: number;
    status: 'PENDING' | 'CONFIRMED' | 'COMPLETED' | 'CANCELLED' | 'BLOCKED';
    notes?: string;
    avatarColor?: string;
    isNewCustomer?: boolean;
    email?: string;
    phone?: string;
}

interface ServiceItem {
    id: number;
    name: string;
    category: 'Men' | 'Women';
    duration: number;
    price: number;
    description?: string;
    requiresCall?: boolean;
}

const Appointments: React.FC = () => {
    // Calendar State
    const [currentDate, setCurrentDate] = useState(new Date());
    const [selectedDate, setSelectedDate] = useState<string>(new Date().toISOString().split('T')[0]);
    const [blockedDates, setBlockedDates] = useState<string[]>([]);

    // Services State
    const [services, setServices] = useState<ServiceItem[]>([]);
    const [activeServiceTab, setActiveServiceTab] = useState<'Men' | 'Women'>('Men');
    const [showServicesModal, setShowServicesModal] = useState(false);
    const [newServiceForm, setNewServiceForm] = useState({ name: '', price: 0, duration: 30, description: '', requiresCall: false });
    const [editingServiceId, setEditingServiceId] = useState<number | null>(null);

    // Appointments State
    const [appointments, setAppointments] = useState<Appointment[]>([]);
    const [loadingAppointments, setLoadingAppointments] = useState(true);
    const [errorAppointments, setErrorAppointments] = useState<string | null>(null);

    // Modal State for New Appointment
    const [showModal, setShowModal] = useState(false);
    const [newAppt, setNewAppt] = useState({
        customerName: '',
        time: '09:00',
        service: '',
        staffName: '',
        duration: '30',
        cost: 0,
        isNewCustomer: false,
        email: '',
        phone: '',
        notes: ''
    });

    // ------------------- Services Handlers -------------------
    const loadServices = () => {
        adminAppointmentsService.getServices()
            .then(data => setServices(data || []))
            .catch(() => setServices([]));
    };

    const handleAddService = (e: React.FormEvent) => {
        e.preventDefault();
        const payload = { ...newServiceForm, category: activeServiceTab };

        if (editingServiceId) {
            adminAppointmentsService.updateService(editingServiceId, payload)
                .then(() => {
                    loadServices();
                    setEditingServiceId(null);
                    setNewServiceForm({ name: '', price: 0, duration: 30, description: '', requiresCall: false });
                });
            return;
        }

        adminAppointmentsService.createService(payload)
            .then(() => {
                loadServices();
                setNewServiceForm({ name: '', price: 0, duration: 30, description: '', requiresCall: false });
            });
    };

    const handleEditService = (service: ServiceItem) => {
        setNewServiceForm({
            name: service.name,
            price: service.price,
            duration: service.duration,
            description: service.description || '',
            requiresCall: service.requiresCall || false
        });
        setEditingServiceId(service.id);
        setActiveServiceTab(service.category);
    };

    const handleCancelEdit = () => {
        setNewServiceForm({ name: '', price: 0, duration: 30, description: '', requiresCall: false });
        setEditingServiceId(null);
    };

    const handleDeleteService = (id: number) => {
        if (window.confirm('Διαγραφή υπηρεσίας;')) {
            adminAppointmentsService.deleteService(id)
                .then(() => {
                    setServices(prev => prev.filter(s => s.id !== id));
                    if (editingServiceId === id) handleCancelEdit();
                });
        }
    };

    const handleServiceSelect = (serviceName: string) => {
        const found = services.find(s => s.name === serviceName);
        if (found) {
            setNewAppt(prev => ({
                ...prev,
                service: serviceName,
                duration: found.duration.toString(),
                cost: found.price
            }));
        } else {
            setNewAppt(prev => ({ ...prev, service: serviceName }));
        }
    };

    const resetForm = () => {
        setNewAppt({
            customerName: '',
            time: '09:00',
            service: '',
            staffName: '',
            duration: '30',
            cost: 0,
            isNewCustomer: false,
            email: '',
            phone: '',
            notes: ''
        });
    };

    // ------------------- Fetch Appointments -------------------
    const fetchAppointments = () => {
        const year = currentDate.getFullYear();
        const month = currentDate.getMonth();
        const from = `${year}-${String(month + 1).padStart(2, '0')}-01`;
        const to = `${year}-${String(month + 1).padStart(2, '0')}-${String(getDaysInMonth(year, month)).padStart(2, '0')}`;

        setLoadingAppointments(true);
        adminAppointmentsService.getAppointments(from, to)
            .then((data) => {
                setAppointments(data || []);
                setErrorAppointments(null);
            })
            .catch(() => setErrorAppointments('Failed to load appointments.'))
            .finally(() => setLoadingAppointments(false));
    };

    useEffect(() => fetchAppointments(), [currentDate]);
    useEffect(() => {
        loadServices();
    }, []);

    // ------------------- New Appointment -------------------
    const handleSaveNewAppointment = (e: React.FormEvent) => {
        e.preventDefault();
        const nameParts = newAppt.customerName.trim().split(' ');
        const firstName = nameParts[0] || '';
        const lastName = nameParts.slice(1).join(' ') || '';

        appointmentService.createAppointment(
            newAppt.service,
            selectedDate,
            newAppt.time,
            newAppt.cost,
            Number(newAppt.duration),
            firstName,
            lastName,
            newAppt.email,
            newAppt.phone,
            newAppt.notes
        ).then(() => fetchAppointments())
            .finally(() => {
                setShowModal(false);
                resetForm();
            });
    };

    // ------------------- Calendar Logic -------------------
    const getDaysInMonth = (year: number, month: number) => new Date(year, month + 1, 0).getDate();
    const getFirstDayOfMonth = (year: number, month: number) => new Date(year, month, 1).getDay();

    const handlePrevMonth = () => setCurrentDate(new Date(currentDate.getFullYear(), currentDate.getMonth() - 1, 1));
    const handleNextMonth = () => setCurrentDate(new Date(currentDate.getFullYear(), currentDate.getMonth() + 1, 1));
    const handleDateClick = (day: number) => setSelectedDate(`${currentDate.getFullYear()}-${String(currentDate.getMonth() + 1).padStart(2, '0')}-${String(day).padStart(2, '0')}`);

    const toggleDateBlocked = () => {
        setBlockedDates(prev => prev.includes(selectedDate) ? prev.filter(d => d !== selectedDate) : [...prev, selectedDate]);
    };

    const handleBlockTime = () => {
        const time = prompt("Εισάγετε ώρα έναρξης που δεν θα είστε διαθέσιμοι (π.χ. 14:00)");
        if (!time) return;
        const duration = prompt("Διάρκεια (λεπτά)", "60");

        const newBlock: Appointment = {
            id: Date.now(),
            customerName: 'ΜΗ ΔΙΑΘΕΣΙΜΟ',
            date: selectedDate,
            time: time,
            service: 'Διάλειμμα / Εκτός',
            staffName: '-',
            duration: duration ? Number(duration) : 0,
            cost: 0,
            status: 'BLOCKED',
            avatarColor: '#555',
            isNewCustomer: false
        };
        setAppointments(prev => [...prev, newBlock]);
    };

    // ------------------- Delete / Cancel Appointment -------------------
    const handleDelete = (id: number) => {
        const app = appointments.find(a => a.id === id);
        if (!app) return;

        if (app.status === 'BLOCKED') {
            if (window.confirm("Να διαγραφεί αυτό το διάστημα μη διαθεσιμότητας;")) {
                setAppointments(prev => prev.filter(a => a.id !== id));
            }
            return;
        }

        if (window.confirm(`Είστε σίγουροι ότι θέλετε να ακυρώσετε το ραντεβού του/της ${app.customerName};`)) {
            handleStatusChange(id, 'CANCELLED');
            const notify = window.confirm(`Να σταλεί ενημέρωση στο ${app.email || 'email'} / ${app.phone || 'κινητό'};`);
            if (notify) alert(`Στάλθηκε ενημέρωση.`);
        }
    };

    const handleContact = (method: 'email' | 'sms', app: Appointment) => {
        alert(`Άνοιγμα ${method === 'email' ? 'Email' : 'SMS'} για ${app.customerName}`);
    };

    const handleStatusChange = (id: number, newStatus: Appointment['status']) => {
        const previousStatus = appointments.find(app => app.id === id)?.status;
        setAppointments(prev => prev.map(app => app.id === id ? { ...app, status: newStatus } : app));
        adminAppointmentsService.updateStatus(id, newStatus).catch(() => {
            if (previousStatus) {
                setAppointments(prev => prev.map(app => app.id === id ? { ...app, status: previousStatus } : app));
            }
            alert('Αποτυχία ενημέρωσης κατάστασης ραντεβού.');
        });
    };

    // ------------------- Render Calendar -------------------
    const renderCalendar = () => {
        const year = currentDate.getFullYear();
        const month = currentDate.getMonth();
        const daysInMonth = getDaysInMonth(year, month);
        const firstDay = getFirstDayOfMonth(year, month);
        const days = [];
        const monthNames = ["Ιανουάριος", "Φεβρουάριος", "Μάρτιος", "Απρίλιος", "Μάιος", "Ιούνιος", "Ιούλιος", "Αύγουστος", "Σεπτέμβριος", "Οκτώβριος", "Νοέμβριος", "Δεκέμβριος"];

        for (let i = 0; i < firstDay; i++) days.push(<div key={`empty-${i}`} className="calendar-day empty"></div>);

        for (let day = 1; day <= daysInMonth; day++) {
            const dateString = `${year}-${String(month + 1).padStart(2, '0')}-${String(day).padStart(2, '0')}`;
            const hasAppointments = appointments.some(app => app.date === dateString);
            const isSelected = selectedDate === dateString;
            const isToday = new Date().toISOString().split('T')[0] === dateString;
            const isBlocked = blockedDates.includes(dateString);

            days.push(
                <div key={day} className={`calendar-day ${isSelected ? 'selected' : ''} ${isToday ? 'today' : ''} ${isBlocked ? 'blocked' : ''}`} onClick={() => handleDateClick(day)}>
                    <span className="day-number">{day}</span>
                    {isBlocked ? <div className="blocked-indicator">✖</div> : (hasAppointments && <div className="appointment-dot"></div>)}
                </div>
            );
        }

        return (
            <div className="calendar-container">
                <div className="calendar-header">
                    <button onClick={handlePrevMonth}>&lt;</button>
                    <h3>{monthNames[month]} {year}</h3>
                    <button onClick={handleNextMonth}>&gt;</button>
                </div>
                <div className="calendar-grid">
                    <div className="weekday">Κυρ</div>
                    <div className="weekday">Δευ</div>
                    <div className="weekday">Τρι</div>
                    <div className="weekday">Τετ</div>
                    <div className="weekday">Πεμ</div>
                    <div className="weekday">Παρ</div>
                    <div className="weekday">Σαβ</div>
                    {days}
                </div>
            </div>
        );
    };

    const selectedDayAppointments = appointments.filter(app => app.date === selectedDate).sort((a, b) => a.time.localeCompare(b.time));
    const isSelectedDateBlocked = blockedDates.includes(selectedDate);
    return (
        <div className="admin-container">
            <SideBar />
            <div className="admin-content">
                <div className="page-header">
                    <h2>Διαχείριση Ραντεβού</h2>
                    <button className="services-config-btn" onClick={() => setShowServicesModal(true)}>
                        ⚙️ Υπηρεσίες
                    </button>
                </div>

                <div className="appointments-layout">
                    {/* Left Side: Calendar */}
                    <div className="calendar-section">
                        {renderCalendar()}
                    </div>
                
                    {/* Right Side: Appointment List */}
                    <div className="appointments-list-section">
                        <div className="list-header">
                            <div>
                                <h3>{selectedDate.split('-').reverse().join('/')}</h3>
                                {loadingAppointments && <span style={{ fontSize: '12px', color: '#666' }}>Loading...</span>}
                                {errorAppointments && <span style={{ fontSize: '12px', color: '#dc3545' }}>{errorAppointments}</span>}
                                {isSelectedDateBlocked && <span style={{ fontSize: '12px', color: '#dc3545', fontWeight: 'bold' }}>● Μη Διαθέσιμη</span>}
                            </div>
                            {/* <div style={{ display: 'flex', gap: '10px' }}>
                                <button
                                    className={`toggle-block-btn ${isSelectedDateBlocked ? 'unblock' : 'block'}`}
                                    onClick={toggleDateBlocked}
                                    title={isSelectedDateBlocked ? "Άνοιγμα Ημερομηνίας" : "Κλείσιμο Ημερομηνίας"}
                                >
                                    {isSelectedDateBlocked ? '🔓' : '🔒'}
                                </button>
                                <button
                                    className="add-appt-btn"
                                    onClick={handleBlockTime}
                                    disabled={isSelectedDateBlocked}
                                    style={{ backgroundColor: '#6c757d', marginRight: '0' }}
                                    title="Προσθήκη Διαλείμματος / Μη διαθέσιμης ώρας"
                                >
                                    ⛔
                                </button>
                                <button
                                    className="add-appt-btn"
                                    onClick={() => setShowModal(true)}
                                    disabled={isSelectedDateBlocked}
                                >
                                    + Νέο
                                </button>
                            </div>  */}
                             
                        </div>
                        {/* New Appointment Modal Form */}
                        {showModal && (
                            <div className="modal-overlay">
                                <div className="modal-content">
                                    <div className="modal-header">
                                        <h3>Νέο Ραντεβού ({selectedDate.split('-').reverse().join('/')})</h3>
                                        <button onClick={() => setShowModal(false)} className="close-modal">✖</button>
                                    </div>
                                    <form onSubmit={handleSaveNewAppointment}>
                                        <div className="form-group">
                                            <label>Ονοματεπώνυμο</label>
                                            <input required type="text" value={newAppt.customerName} onChange={e => setNewAppt({ ...newAppt, customerName: e.target.value })} placeholder="π.χ. Γιάννης Παππάς" />
                                        </div>
                                        <div className="form-row">
                                            <div className="form-group half">
                                                <label>Ώρα</label>
                                                <input required type="time" value={newAppt.time} onChange={e => setNewAppt({ ...newAppt, time: e.target.value })} />
                                            </div>
                                            <div className="form-group half">
                                                <label>Υπηρεσία</label>
                                                <select
                                                    required
                                                    value={newAppt.service}
                                                    onChange={e => handleServiceSelect(e.target.value)}
                                                    style={{ width: '100%', padding: '8px', border: '1px solid #ddd', borderRadius: '6px' }}
                                                >
                                                    <option value="">Επιλέξτε...</option>
                                                    <optgroup label="Ανδρικό">
                                                        {services.filter(s => s.category === 'Men').map(s => (
                                                            <option key={s.id} value={s.name}>{s.name}</option>
                                                        ))}
                                                    </optgroup>
                                                    <optgroup label="Γυναικείο">
                                                        {services.filter(s => s.category === 'Women').map(s => (
                                                            <option key={s.id} value={s.name}>{s.name}</option>
                                                        ))}
                                                    </optgroup>
                                                </select>
                                            </div>
                                        </div>
                                        <div className="form-row">
                                            <div className="form-group half">
                                                <label>Διάρκεια (λεπτά)</label>
                                                <input required type="number" value={newAppt.duration} onChange={e => setNewAppt({ ...newAppt, duration: e.target.value })} />
                                            </div>
                                            <div className="form-group half">
                                                <label>Κόστος (€)</label>
                                                <input required type="number" value={newAppt.cost} onChange={e => setNewAppt({ ...newAppt, cost: Number(e.target.value) })} />
                                            </div>
                                        </div>
                                        <div className="form-group">
                                            <label>Υπάλληλος</label>
                                            <input required type="text" value={newAppt.staffName} onChange={e => setNewAppt({ ...newAppt, staffName: e.target.value })} placeholder="π.χ. Μαρία" />
                                        </div>
                                        <div className="form-row">
                                            <div className="form-group half">
                                                <label>Email</label>
                                                <input type="email" value={newAppt.email} onChange={e => setNewAppt({ ...newAppt, email: e.target.value })} />
                                            </div>
                                            <div className="form-group half">
                                                <label>Τηλέφωνο</label>
                                                <input type="tel" value={newAppt.phone} onChange={e => setNewAppt({ ...newAppt, phone: e.target.value })} />
                                            </div>
                                        </div>
                                        <div className="form-group checkbox-group">
                                            <label>
                                                <input type="checkbox" checked={newAppt.isNewCustomer} onChange={e => setNewAppt({ ...newAppt, isNewCustomer: e.target.checked })} />
                                                Νέος Πελάτης;
                                            </label>
                                        </div>
                                        <div className="modal-actions">
                                            <button type="button" onClick={() => setShowModal(false)} className="cancel-btn">Ακύρωση</button>
                                            <button type="submit" className="save-btn">Αποθήκευση</button>
                                        </div>
                                    </form>
                                </div>
                            </div>
                        )}

                        {/* Services Configuration Modal */}
                        {showServicesModal && (
                            <div className="modal-overlay">
                                <div className="modal-content" style={{ width: '500px' }}>
                                    <div className="modal-header">
                                        <h3>Διαχείριση Υπηρεσιών</h3>
                                        <button onClick={() => setShowServicesModal(false)} className="close-modal">✖</button>
                                    </div>

                                    <div className="services-tabs">
                                        <button
                                            className={`tab-btn ${activeServiceTab === 'Men' ? 'active' : ''}`}
                                            onClick={() => setActiveServiceTab('Men')}
                                        >
                                            🤵 Ανδρικό
                                        </button>
                                        <button
                                            className={`tab-btn ${activeServiceTab === 'Women' ? 'active' : ''}`}
                                            onClick={() => setActiveServiceTab('Women')}
                                        >
                                            👰 Γυναικείο
                                        </button>
                                    </div>

                                    <div className="services-list-container">
                                        <ul className="services-list">
                                            {services.filter(s => s.category === activeServiceTab).map(s => (
                                                <li key={s.id} className="service-item">
                                                    <div className="svc-info">
                                                        <span className="svc-name">{s.name}</span>
                                                        <span className="svc-details">{s.duration}' / {s.price}€</span>
                                                        {s.requiresCall && <span className="svc-desc">Requires phone consultation</span>}
                                                        {s.description && <span className="svc-desc">{s.description}</span>}
                                                    </div>
                                                    <div className="svc-actions">
                                                        <button onClick={() => handleEditService(s)} className="edit-svc-btn">✏️</button>
                                                        <button onClick={() => handleDeleteService(s.id)} className="delete-svc-btn">🗑️</button>
                                                    </div>
                                                </li>
                                            ))}
                                            {services.filter(s => s.category === activeServiceTab).length === 0 && (
                                                <p style={{ color: '#999', textAlign: 'center', fontStyle: 'italic' }}>Δεν υπάρχουν υπηρεσίες.</p>
                                            )}
                                        </ul>
                                    </div>

                                    <form onSubmit={handleAddService} className="add-service-form">
                                        <div className="form-header-row">
                                            <h4>{editingServiceId ? 'Επεξεργασία Υπηρεσίας' : `Προσθήκη Νέας (${activeServiceTab === 'Men' ? 'Ανδρικό' : 'Γυναικείο'})`}</h4>
                                        </div>

                                        <div className="form-group-modern">
                                            <label>Ονομασία</label>
                                            <input
                                                required
                                                type="text"
                                                placeholder="π.χ. Κούρεμα"
                                                value={newServiceForm.name}
                                                onChange={e => setNewServiceForm({ ...newServiceForm, name: e.target.value })}
                                                className="modern-input"
                                            />
                                        </div>

                                        <div className="form-row">
                                            <div className="form-group-modern" style={{ flex: 1 }}>
                                                <label>Διάρκεια (min)</label>
                                                <input
                                                    required
                                                    type="number"
                                                    placeholder="30"
                                                    value={newServiceForm.duration}
                                                    onChange={e => setNewServiceForm({ ...newServiceForm, duration: Number(e.target.value) })}
                                                    className="modern-input"
                                                />
                                            </div>
                                            <div className="form-group-modern" style={{ flex: 1 }}>
                                                <label>Τιμή (€)</label>
                                                <input
                                                    required
                                                    type="number"
                                                    placeholder="15"
                                                    value={newServiceForm.price}
                                                    onChange={e => setNewServiceForm({ ...newServiceForm, price: Number(e.target.value) })}
                                                    className="modern-input"
                                                />
                                            </div>
                                        </div>

                                        <div className="form-group-modern">
                                            <label>Περιγραφή</label>
                                            <textarea
                                                placeholder="Σύντομη περιγραφή υπηρεσίας..."
                                                value={newServiceForm.description}
                                                onChange={e => setNewServiceForm({ ...newServiceForm, description: e.target.value })}
                                                className="modern-input modern-textarea"
                                                rows={2}
                                            />
                                        </div>

                                        <div className="form-group-modern" style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                                            <input
                                                id="requiresCall"
                                                type="checkbox"
                                                checked={newServiceForm.requiresCall}
                                                onChange={e => setNewServiceForm({ ...newServiceForm, requiresCall: e.target.checked })}
                                            />
                                            <label htmlFor="requiresCall" style={{ margin: 0 }}>Requires phone consultation</label>
                                        </div>

                                        <div className="form-actions" style={{ display: 'flex', gap: '10px', marginTop: '15px' }}>
                                            {editingServiceId && (
                                                <button type="button" onClick={handleCancelEdit} className="cancel-svc-btn" style={{ flex: 1 }}>Ακύρωση</button>
                                            )}
                                            <button type="submit" className="add-svc-submit-btn" style={{ flex: 1 }}>{editingServiceId ? 'Αποθήκευση' : 'Προσθήκη'}</button>
                                        </div>
                                    </form>
                                </div>
                            </div>
                        )}

                        <div className="appointments-cards-container">
                            {isSelectedDateBlocked ? (
                                <div className="no-appts-placeholder blocked-placeholder">
                                    <div style={{ fontSize: '40px', marginBottom: '10px' }}>⛔</div>
                                    <p>Η ημερομηνία είναι κλειστή για ραντεβού.</p>
                                </div>
                            ) : (
                                selectedDayAppointments.length > 0 ? (
                                    selectedDayAppointments.map(app => (
                                        <div key={app.id} className={`appt-card ${app.status === 'BLOCKED' ? 'blocked-card-slot' : ''}`}>
                                            <div className="appt-time-strip" style={app.status === 'BLOCKED' ? { backgroundColor: '#dc3545', borderRightColor: '#dc3545' } : {}}>
                                                {app.time}
                                            </div>
                                            {app.status === 'BLOCKED' ? (
                                                <div className="appt-details blocked-details" style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', minHeight: '120px', padding: '0 20px' }}>
                                                    <div style={{ display: 'flex', alignItems: 'center', gap: '15px' }}>
                                                        <div style={{ fontSize: '32px' }}>⛔</div>
                                                        <div>
                                                            <div style={{ fontWeight: 'bold', color: '#dc3545', fontSize: '16px' }}>ΜΗ ΔΙΑΘΕΣΙΜΟ</div>
                                                            <div style={{ color: '#777', fontSize: '13px' }}>{app.service} ({app.duration} min)</div>
                                                        </div>
                                                    </div>
                                                    <button onClick={() => handleDelete(app.id)} className="delete-appt-btn" title="Διαγραφή">🗑️</button>
                                                </div>
                                            ) : (
                                                <div className="appt-details">
                                                    <div className="appt-header">
                                                        <div className="customer-info">
                                                            <div className="avatar" style={{ backgroundColor: app.avatarColor || '#ccc' }}>
                                                                {app.customerName.charAt(0)}
                                                            </div>
                                                            <div style={{ display: 'flex', flexDirection: 'column', lineHeight: '1.2' }}>
                                                                <span className="customer-name">{app.customerName}</span>
                                                                {app.isNewCustomer ? (
                                                                    <span className="customer-badge new">ΝΕΟΣ ΠΕΛΑΤΗΣ</span>
                                                                ) : (
                                                                    <span className="customer-badge returning">ΠΑΛΙΟΣ ΠΕΛΑΤΗΣ</span>
                                                                )}
                                                            </div>
                                                        </div>
                                                        <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                                                            <select
                                                                className={`status-select ${app.status.toLowerCase()}`}
                                                                value={app.status}
                                                                onChange={(e) => handleStatusChange(app.id, e.target.value as Appointment['status'])}
                                                            >
                                                                <option value="PENDING">Εκκρεμεί</option>
                                                                <option value="CONFIRMED">Επιβεβαιωμένο</option>
                                                                <option value="COMPLETED">Ολοκληρώθηκε</option>
                                                                <option value="CANCELLED">Ακυρώθηκε</option>
                                                            </select>
                                                            <div className="action-buttons">
                                                                <button onClick={() => handleContact('email', app)} className="contact-btn" title="Αποστολή Email">✉️</button>
                                                                <button onClick={() => handleContact('sms', app)} className="contact-btn" title="Αποστολή SMS">📱</button>
                                                                <button onClick={() => handleDelete(app.id)} className="delete-appt-btn" title="Διαγραφή / Ακύρωση">🗑️</button>
                                                            </div>
                                                        </div>
                                                    </div>

                                                    <div className="appt-info-grid">
                                                        <div className="info-item">
                                                            <span className="label">Υπηρεσία</span>
                                                            <span className="value">{app.service}</span>
                                                        </div>
                                                        <div className="info-item">
                                                            <span className="label">Με ποιον</span>
                                                            <span className="value">{app.staffName}</span>
                                                        </div>
                                                        <div className="info-item">
                                                            <span className="label">Τηλέφωνο</span>
                                                            <span className="value">{app.phone || '-'}</span>
                                                        </div>
                                                        <div className="info-item">
                                                            <span className="label">Διάρκεια</span>
                                                            <span className="value">{app.duration} min</span>
                                                        </div>
                                                        <div className="info-item">
                                                            <span className="label">Κόστος</span>
                                                            <span className="value">{app.cost}€</span>
                                                        </div>
                                                    </div>

                                                    {app.notes && (
                                                        <div className="appt-notes">
                                                            📝 {app.notes}
                                                        </div>
                                                    )}
                                                </div>
                                            )}
                                        </div>
                                    ))
                                ) : (
                                    <div className="no-appts-placeholder">
                                        <p>Δεν υπάρχουν ραντεβού για αυτήν την ημέρα.</p>
                                    </div>
                                ))}
                        </div>
                    </div>
                </div>
            </div>
        </div>
    );
};

export default Appointments;
